"use client";

import { useState } from "react";
import { Send, CheckCircle2, AlertCircle } from "lucide-react";

export function ContactForm() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    service: "",
    message: ""
  });

  const [status, setStatus] = useState({
    loading: false,
    success: false,
    error: null
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus({ loading: true, success: false, error: null });

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to send message');
      }

      setStatus({ loading: false, success: true, error: null });
      setFormData({
        name: "",
        email: "",
        phone: "",
        service: "",
        message: ""
      });
    } catch (error) {
      setStatus({ loading: false, success: false, error: error.message });
    }
  };

  return (
    <div className='w-full'>
      <div className='bg-gradient-to-br from-primary/20 to-primary/10 rounded-3xl'>
        <div className='p-8 lg:p-12'>
          <div className='max-w-xl'>
            <h2 className='text-2xl lg:text-3xl font-medium tracking-tight text-primary mb-4'>
              Request a Consultation
            </h2>
            <p className='text-sm lg:text-base text-secondary mb-8'>
              Sr Diana will personally review your enquiry and contact you to discuss your care needs.
            </p>
          </div>

          <form onSubmit={handleSubmit} className='space-y-6'>
            <div className='grid grid-cols-1 sm:grid-cols-2 gap-6'>
              <div>
                <label htmlFor='name' className='block text-sm font-medium text-primary mb-2'>
                  Full Name
                </label>
                <input
                  type='text'
                  id='name'
                  name='name'
                  required
                  className='w-full px-4 py-3 rounded-lg bg-white border border-primary/20 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-colors'
                  placeholder='Your name'
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                />
              </div>

              <div>
                <label htmlFor='email' className='block text-sm font-medium text-primary mb-2'>
                  Email Address
                </label>
                <input
                  type='email'
                  id='email'
                  name='email'
                  required
                  className='w-full px-4 py-3 rounded-lg bg-white border border-primary/20 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-colors'
                  placeholder='your.email@example.com'
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                />
              </div>
            </div>

            <div className='grid grid-cols-1 sm:grid-cols-2 gap-6'>
              <div>
                <label htmlFor='phone' className='block text-sm font-medium text-primary mb-2'>
                  Phone Number
                </label>
                <input
                  type='tel'
                  id='phone'
                  name='phone'
                  className='w-full px-4 py-3 rounded-lg bg-white border border-primary/20 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-colors'
                  placeholder='Your contact number'
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                />
              </div>

              <div>
                <label htmlFor='service' className='block text-sm font-medium text-primary mb-2'>
                  Type of Care Needed
                </label>
                <select
                  id='service'
                  name='service'
                  className='w-full px-4 py-3 rounded-lg bg-white border border-primary/20 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-colors'
                  value={formData.service}
                  onChange={(e) => setFormData({ ...formData, service: e.target.value })}
                >
                  <option value=''>Select care type</option>
                  <option value='post-hospital'>Post-Hospital Care</option>
                  <option value='chronic'>Chronic Disease Management</option>
                  <option value='elderly'>Elderly Care</option>
                  <option value='palliative'>Palliative Care</option>
                  <option value='specialised'>Specialised Care (Dementia, Stroke, etc.)</option>
                  <option value='daily'>Daily Living Support</option>
                </select>
              </div>
            </div>

            <div>
              <label htmlFor='message' className='block text-sm font-medium text-primary mb-2'>
                Care Requirements
              </label>
              <textarea
                id='message'
                name='message'
                rows={4}
                required
                className='w-full px-4 py-3 rounded-lg bg-white border border-primary/20 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-colors'
                placeholder='Please describe your care needs or any specific requirements...'
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
              />
            </div>

            {status.error && (
              <div className='flex items-center gap-2 text-red-600'>
                <AlertCircle className='w-5 h-5' />
                <p className='text-sm'>{status.error}</p>
              </div>
            )}

            {status.success && (
              <div className='flex items-center gap-2 text-green-600'>
                <CheckCircle2 className='w-5 h-5' />
                <p className='text-sm'>Thank you for your inquiry. We will get back to you soon!</p>
              </div>
            )}

            <button
              type='submit'
              disabled={status.loading}
              className='inline-flex items-center justify-center gap-2 bg-primary hover:bg-primary/90 disabled:bg-primary/50 text-white px-6 py-3 rounded-lg transition-colors font-medium group'
            >
              {status.loading ? (
                'Sending...'
              ) : (
                <>
                  Send Enquiry
                  <Send className='w-4 h-4 transition-transform group-hover:translate-x-0.5' />
                </>
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
